import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Trophy, ShieldAlert, AlertCircle, Inbox } from 'lucide-react'
import { useGetCompetitionLeaderboard } from '../hooks/backend/performance'
import { BoardLinkButton } from '../components/drivers/BoardLinkButton'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Chip } from '../components/interviews/Chip'
import {
  type Leaderboard,
  type CompetitionRow,
  gateLabel,
  STANDING_TEXT,
  rand,
  fixed,
  percent,
  weekLabel,
} from '../utils/competition'
import { ApprovalPanel, RuleReviewPanel, ZoneGuidePanel, CoachingLoader } from '../components/performance/CompetitionReview'

/**
 * The weekly driver competition.
 *
 * Top earners win, but only among drivers who pass every gate. The page leads
 * with who is still in it, then shows where the rest fell, because the gate
 * table is the part that changes behaviour: a man who can see that most of the
 * fleet is losing on late forms knows exactly what to do next week.
 *
 * Nothing is computed here. The standings are recomputed in Postgres every
 * hour for the open week and on Monday at 12:00 for the week just closed.
 */
export default function Competition() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const [week, setWeek] = useState<string | null>(null)
  const feed = useGetCompetitionLeaderboard()
  const trigger = feed.trigger

  const load = useCallback(() => {
    trigger(week ? { week_start: week } : {}, { skipCache: true })
  }, [trigger, week])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const board = (feed.data as Leaderboard | null | undefined) ?? null
  const rows = board?.rows ?? []

  const counts = useMemo(() => {
    const c = { qualified: 0, in_contention: 0, pending: 0, out: 0 }
    for (const r of rows) c[r.standing] += 1
    return c
  }, [rows])

  const alive = rows.filter((r) => r.standing !== 'out')
  const out = rows.filter((r) => r.standing === 'out')

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">The competition board needs view access to the cockpit.</p>
        </div>
      </div>
    )
  }

  const total = rows.length
  const prizes = board?.prizes ?? []

  return (
    <div className="h-full overflow-y-auto bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Trophy className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Driver competition</h1>
          <a
            href="/board"
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            title="The drivers' own screen. Open to staff with driver board access."
          >
            Open the driver board
          </a>
          {board && (
            <select
              aria-label="Week"
              className="ml-auto rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={board.week_start}
              onChange={(e) => setWeek(e.target.value)}
            >
              {Array.from(new Set([board.week_start, ...board.weeks])).map((w) => (
                <option key={w} value={w}>
                  Week of {weekLabel(w, addDays(w, 6))}
                </option>
              ))}
            </select>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {board
            ? `${weekLabel(board.week_start, board.week_end)}. ${board.week_open ? 'Week in progress, updated every hour.' : statusText(board.status)} Prizes ${prizes.map((p) => rand(p)).join(', ')}.`
            : caption}
        </p>
        {board?.notes && <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">{board.notes}</p>}

        {board && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Chip tone={counts.qualified > 0 ? 'green' : 'neutral'}>
              Qualify now <span className="ml-1 font-semibold tabular-nums">{counts.qualified}</span>
            </Chip>
            {board.week_open && (
              <Chip tone="amber">
                Still in it <span className="ml-1 font-semibold tabular-nums">{counts.in_contention}</span>
              </Chip>
            )}
            {counts.pending > 0 && (
              <Chip tone="neutral">
                Waiting on data <span className="ml-1 font-semibold tabular-nums">{counts.pending}</span>
              </Chip>
            )}
            <Chip tone="red">
              Out this week <span className="ml-1 font-semibold tabular-nums">{counts.out}</span>
            </Chip>
            <Chip tone="neutral">
              Drivers <span className="ml-1 font-semibold tabular-nums">{total}</span>
            </Chip>
          </div>
        )}
      </div>

      {feed.error ? (
        <div className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{String(feed.error)}</span>
        </div>
      ) : feed.loading && !board ? (
        <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Loading the leaderboard…
        </div>
      ) : !board || total === 0 ? (
        <div className="m-4 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">
          <Inbox className="h-8 w-8 opacity-40" />
          <p className="text-sm font-medium text-foreground">No shifts in this week yet</p>
          <p className="text-sm">The board fills in as shifts are submitted.</p>
        </div>
      ) : (
        <div className="mx-auto max-w-6xl space-y-6 p-4">
          {!board.week_open && <ApprovalPanel weekStart={board.week_start} />}

          <section>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {board.week_open ? 'Still in the running' : 'Qualified'}
            </h2>
            {alive.length === 0 ? (
              <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                {board.week_open
                  ? 'Nobody is still in it this week. Every driver has already failed a gate that cannot be undone before Sunday.'
                  : 'Nobody passed every gate this week, so no prize is paid.'}
              </p>
            ) : (
              <BoardTable rows={alive} showPrize prizeHeader={board.week_open ? 'Prize if it ended now' : 'Prize'} />
            )}
            {board.week_open && alive.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                "Still in it" means the only gates missed so far are averages that can still come right by Sunday night:
                earnings, online hours, acceptance, fare, fuel or rating.
              </p>
            )}
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold text-foreground">Where drivers are falling</h2>
            <div className="space-y-1.5">
              {board.gate_summary
                .filter((g) => g.fail > 0 || g.pending > 0)
                .map((g) => (
                  <div key={g.gate} className="flex items-center gap-3 text-sm">
                    <span className="w-72 shrink-0 truncate text-foreground" title={gateLabel(g.gate)}>
                      {gateLabel(g.gate)}
                    </span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-[hsl(var(--brand))]"
                        style={{ width: `${total ? (g.fail / total) * 100 : 0}%` }}
                      />
                    </div>
                    <span className="w-24 shrink-0 text-right tabular-nums text-muted-foreground">
                      {g.fail} of {total}
                      {g.pending > 0 ? `, ${g.pending} ?` : ''}
                    </span>
                  </div>
                ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              A driver can fail more than one gate, so these do not add up to the number of drivers. "?" is a driver whose
              data for that gate has not arrived, who is never counted as passing it.
            </p>
          </section>

          <section>
            <h2 className="mb-2 text-sm font-semibold text-foreground">Out this week</h2>
            <BoardTable rows={out} />
          </section>

          <RuleReviewPanel />

          <ZoneGuidePanel />
        </div>
      )}
    </div>
  )
}

function BoardTable({
  rows,
  showPrize = false,
  prizeHeader = 'Prize',
}: {
  rows: CompetitionRow[]
  showPrize?: boolean
  prizeHeader?: string
}) {
  const [open, setOpen] = useState<string | null>(null)
  const columns = showPrize ? 13 : 12
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">Driver</th>
            <th className="px-3 py-2">Standing</th>
            <th className="px-3 py-2 text-right">Earnings</th>
            <th className="px-3 py-2 text-right">Shifts</th>
            <th className="px-3 py-2 text-right">Per shift</th>
            <th className="px-3 py-2 text-right">Hours</th>
            <th className="px-3 py-2 text-right">Accept</th>
            <th className="px-3 py-2 text-right">Per trip</th>
            <th className="whitespace-nowrap px-3 py-2 text-right">Fuel / std</th>
            {showPrize && <th className="px-3 py-2 text-right">{prizeHeader}</th>}
            <th className="px-3 py-2">Missed</th>
            <th className="px-3 py-2">Driver board</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const s = STANDING_TEXT[r.standing]
            const missed = [...(r.failed_gates ?? []).map(gateLabel), ...(r.pending_gates ?? []).map((g) => `${gateLabel(g)} (no data yet)`)]
            return (
              <Fragment key={r.driver_id}>
              <tr className="border-t border-border align-top">
                <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.earnings_rank ?? '—'}</td>
                <td className="px-3 py-2 font-medium text-foreground">
                  {r.driver_name ?? 'Unnamed driver'}
                  <button
                    type="button"
                    onClick={() => setOpen(open === r.driver_id ? null : r.driver_id)}
                    className="ml-2 rounded border border-border px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground hover:bg-muted"
                  >
                    {open === r.driver_id ? 'Hide coaching' : 'Coaching'}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <Chip tone={s.tone}>{s.label}</Chip>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{rand(r.total_earnings_zar)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.shifts ?? '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{rand(r.earnings_per_shift_zar)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fixed(r.online_hours_avg)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{percent(r.acceptance_avg)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{rand(r.earnings_per_trip_zar)}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                  {fixed(r.l_per_100km, 2)} / {fixed(r.fuel_standard_l_per_100km, 1)}
                </td>
                {showPrize && (
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {Number(r.prize_zar) > 0 ? rand(r.prize_zar) : '—'}
                  </td>
                )}
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {missed.length === 0 ? 'Nothing' : missed.join('; ')}
                </td>
                <td className="px-3 py-2">
                  <BoardLinkButton driverId={r.driver_id} linked={r.board_linked === true} />
                </td>
              </tr>
              {open === r.driver_id && (
                <tr className="border-t border-border bg-muted/30">
                  <td colSpan={columns} className="px-3 py-3">
                    <CoachingLoader driverId={r.driver_id} />
                  </td>
                </tr>
              )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


function statusText(status: Leaderboard['status']): string {
    if (status === 'approved') return 'Week closed and approved. Prizes are paid with the month-end salary.'
  if (status === 'computed') return 'Week closed, waiting for approval.'
  return 'Not yet computed.'
}

// Date arithmetic on the YYYY-MM-DD string in UTC, so the browser's time zone
// cannot shift the Sunday.
function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}
